import { createPortal } from "react-dom";
import { X, MessageCircle, FileText, Download, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import ProductImage from "@/components/ProductImage";
import { waLink } from "@/lib/constants";

export default function ProductDetailModal({ open, onClose, product, onRequestQuote }) {
    if (!open || !product) return null;

    const onDatasheet = (e) => {
        e.preventDefault();
        toast.info("Datasheet request received — our team will email it to you shortly.");
    };

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6" role="dialog" aria-modal="true" data-testid="product-detail-modal">
            <div className="absolute inset-0 bg-navy/60 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/95 hover:bg-slate-100 text-slate-500 shadow"
                    data-testid="product-detail-close"
                    aria-label="Close"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="flex flex-col md:flex-row overflow-y-auto">
                    <div className="relative w-full md:w-[380px] md:shrink-0 aspect-[4/3] md:aspect-auto md:min-h-[520px]">
                        <ProductImage product={product} size="lg" />
                    </div>
                    <div className="flex-1 p-6 sm:p-8 flex flex-col min-w-0">
                        <div className="text-[11px] font-bold uppercase tracking-widest text-brand">
                            {product.category}
                        </div>
                        <h2 className="mt-2 font-display text-2xl sm:text-3xl font-bold text-navy leading-tight" data-testid="product-detail-name">
                            {product.name}
                        </h2>
                        <div className="mt-2 flex items-center gap-3 text-sm">
                            <span className="text-slate-500">
                                <span className="text-slate-400">Brand:</span>{" "}
                                <span className="font-semibold text-navy">{product.brand}</span>
                            </span>
                            <span className="w-1 h-1 rounded-full bg-slate-300" />
                            <span className="text-slate-500 font-mono text-xs">{product.model_number}</span>
                        </div>

                        <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full w-fit">
                            <CheckCircle2 className="w-3.5 h-3.5" /> {product.availability || "Available"}
                        </div>

                        <p className="mt-4 text-slate-600 leading-relaxed text-sm">{product.short_description}</p>

                        {product.specs?.length > 0 && (
                            <div className="mt-5">
                                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Technical Specifications</h3>
                                <ul className="space-y-1.5">
                                    {product.specs.map((s) => (
                                        <li key={s} className="flex items-start gap-2 text-sm text-navy">
                                            <span className="w-1.5 h-1.5 rounded-full bg-brand mt-2 shrink-0" />
                                            <span>{s}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {product.features?.length > 0 && (
                            <div className="mt-5">
                                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
                                    <Sparkles className="w-3.5 h-3.5 text-brand" /> Product Features
                                </h3>
                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                    {product.features.map((f) => (
                                        <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                                            <CheckCircle2 className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <button
                                onClick={onRequestQuote}
                                data-testid="product-detail-quote-btn"
                                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-brand hover:bg-brand-hover text-white px-4 py-2.5 text-xs font-semibold transition-colors"
                            >
                                <FileText className="w-4 h-4" /> Request Quote
                            </button>
                            <a
                                href={waLink(`Hi CoreCyrus, please quote for ${product.name} (${product.brand} ${product.model_number}).`)}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="product-detail-whatsapp-btn"
                                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#1EBE5A] text-white px-4 py-2.5 text-xs font-semibold transition-colors"
                            >
                                <MessageCircle className="w-4 h-4" /> WhatsApp Inquiry
                            </a>
                            <a
                                href={product.datasheet_url || "#"}
                                onClick={onDatasheet}
                                data-testid="product-detail-datasheet-btn"
                                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-navy px-4 py-2.5 text-xs font-semibold transition-colors"
                            >
                                <Download className="w-4 h-4" /> Datasheet
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
